import { len2 } from "../core/math.js";
import type { Vec2 } from "@golazo/shared";

export interface SweepHit {
  /** Fraccion del tick en la que ocurre el contacto, en [0,1]. */
  t: number;
  /** Normal del contacto, del centro del obstaculo hacia el movil. */
  nx: number;
  ny: number;
  /** true si ya estaban solapados al empezar el tick. */
  overlapped: boolean;
}

/**
 * Colision continua circulo-circulo.
 *
 * Resuelve |P0 + t·D| = R sobre el segmento recorrido en el tick, en vez de
 * preguntar "¿solapan al final?". Sin esto, cualquier movil mas rapido que su
 * propio diametro por tick atraviesa el obstaculo: a 60 Hz un balon a 1.883 u/s
 * avanza 31 u y el diametro balon+jugador es 34, asi que el 24% de los disparos
 * a tope cruzaban el cuerpo del defensor sin tocarlo.
 *
 * `P0` y `D` son RELATIVOS al obstaculo, asi que sirve igual para un obstaculo
 * quieto (postes) que para uno en movimiento (jugadores): basta restar su
 * desplazamiento del desplazamiento del movil.
 *
 * Es matematica cerrada y determinista, requisito de toda la simulacion.
 */
export function sweepCircleVsCircle(
  p0x: number,
  p0y: number,
  dx: number,
  dy: number,
  radius: number,
): SweepHit | null {
  const radiusSq = radius * radius;
  const startSq = p0x * p0x + p0y * p0y;

  // Ya solapados al empezar: contacto inmediato, normal desde donde estaban.
  if (startSq < radiusSq - 1e-4) {
    const dist = Math.sqrt(startSq) || radius;
    return { t: 0, nx: p0x / dist, ny: p0y / dist, overlapped: true };
  }

  const a = dx * dx + dy * dy;
  if (a < 1e-8) return null; // Sin movimiento relativo: nada que barrer.

  const b = 2 * (p0x * dx + p0y * dy);
  const c = startSq - radiusSq;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;

  const sqrtDisc = Math.sqrt(disc);
  let t = (-b - sqrtDisc) / (2 * a);
  if (t < 0 || t > 1) t = (-b + sqrtDisc) / (2 * a);
  if (t < 0 || t > 1) return null;

  const hx = p0x + dx * t;
  const hy = p0y + dy * t;
  const dist = len2(hx, hy) || radius;
  return { t, nx: hx / dist, ny: hy / dist, overlapped: false };
}

/** Azucar para llamar con vectores. */
export function sweepRelative(
  movilPrev: Vec2,
  movilNow: Vec2,
  obstaclePrev: Vec2,
  obstacleNow: Vec2,
  radius: number,
): SweepHit | null {
  return sweepCircleVsCircle(
    movilPrev.x - obstaclePrev.x,
    movilPrev.y - obstaclePrev.y,
    movilNow.x - movilPrev.x - (obstacleNow.x - obstaclePrev.x),
    movilNow.y - movilPrev.y - (obstacleNow.y - obstaclePrev.y),
    radius,
  );
}
